import React from 'react';
import CredentialSelector from './shared/CredentialSelector';
import { useAuth } from '../contexts/AuthContext';
import './AddPaymentPorterWizard.css';

/**
 * Shared credential step used by both the Payment Porter and Payment Migration Manager wizards.
 * Presents credentials the SAME way as the sync task configure page and the payment-porter / vault
 * detail screens: a collapsed "Connection Credentials" entry (a CredentialChip once one is selected,
 * or a "+ Add Credential" button) that opens the canonical full-screen, tabbed CredentialsPicker
 * (CredentialPickerScreen) for choosing or adding at the partner / chain / hotel level.
 *
 * All credential state is managed by useCredentialStep (passed as `cs`); the picker's onSelect /
 * onCreateAndShare map straight onto cs.selectedCredential. Per-hotel overrides (HOTELID etc.) for
 * chain/partner-level credentials are asked inside the picker, with Save (overridesInPicker).
 *
 * Props:
 *   selectedMarketplace  – the currently selected marketplace object
 *   selectedHotel        – the currently selected hotel object
 *   accountId            – selected chain (account) id
 *   partnerId            – effective partner id
 *   cs                   – return value of useCredentialStep(...)
 *   errors               – { connector?, hotelIdOverride? } from parent error state
 *   onClearError(key)    – clears a specific error key in the parent
 *   onBack               – navigate back
 *   onNext               – validate + navigate forward (parent's goToNextStep)
 */
const WizardCredentialsStep = ({ selectedMarketplace, selectedHotel, accountId, partnerId, cs, errors, onClearError, onBack, onNext }) => {
    const { isSuperAdmin } = useAuth();

    const credType      = cs.selectedCredential?._entityType;
    const selAboveHotel = credType === 'account' || credType === 'partner';
    const nextDisabled  = !cs.selectedCredential;

    // Reset per-hotel overrides whenever the selected credential changes.
    const resetOverrides = () => {
        cs.setHotelIdOverride('');
        const fields = {};
        cs.overrideDefs.forEach(d => { fields[d.SETTINGNAME] = ''; });
        cs.setCredentialOverrides(fields);
        onClearError?.('connector');
        onClearError?.('hotelIdOverride');
    };

    const handleSelect = (cred, values) => {
        resetOverrides();
        cs.pickWithOverrides(cred, values);
        // The picker closes itself on select (CredentialSelector owns that state since it absorbed the
        // five hand-copied variants). This used to call a setShowPicker that no longer exists, which
        // threw a ReferenceError on every pick.
    };

    const handleRemove = () => {
        cs.setSelectedCredential(null);
        resetOverrides();
    };

    return (
        <div className="ppw-card">
            <div className="ppw-card-header">
                <h2 className="ppw-card-title">Credentials</h2>
                <p className="ppw-card-subtitle">Select existing {selectedMarketplace?.name} credentials or add new ones</p>
            </div>
            <div className="ppw-card-body">
                {errors?.connector && (
                    <span className="ppw-error-msg" style={{ marginBottom: '1rem', display: 'block' }}>{errors.connector}</span>
                )}

                {/* The shared selector: chip when chosen, "+ Add Credential" when not, full-screen
                    picker behind both. Was five hand-copied variants across the portal. */}
                <CredentialSelector
                    marketplaceId={selectedMarketplace?.id}
                    hotelId={selectedHotel?.hotelid}
                    accountId={accountId}
                    partnerId={partnerId}
                    value={cs.selectedCredential}
                    // The per-hotel fields (HOTELID etc. for a chain/partner credential) are asked IN the picker,
                    // with Save — the shared pattern every product uses.
                    overridesInPicker
                    overrideDefs={cs.overrideDefs.length ? cs.overrideDefs : undefined}
                    overrides={cs.overrideValues}
                    overridesTitle="Per-Hotel Overrides"
                    onChange={(sel, values) => (sel ? handleSelect(sel, values) : handleRemove())}
                    canEdit={isSuperAdmin}
                    title={null}
                    subtitle="Choose an existing credential from the partner, chain or hotel — or add a new one at any level."
                />
                {selAboveHotel && cs.overrideDefs.length > 0 && errors?.hotelIdOverride && (
                    <span className="ppw-error-msg">{errors.hotelIdOverride}</span>
                )}
            </div>
            <div className="ppw-footer">
                <button className="ppw-btn-back" onClick={onBack}>← Back</button>
                <button className="ppw-btn-next" onClick={onNext} disabled={nextDisabled}>Next →</button>
            </div>
        </div>
    );
};

export default WizardCredentialsStep;
